import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Minus, Plus, X } from 'lucide-react';
import B2BLayout from '../../../components/b2b/B2BLayout';
import { PageLeaveConfirmModal, Textarea } from '../../../components/ui';
import { useAppContext } from '../../../context/AppContext';
import sparkleBoldIcon from '../../../assets/icons/24/24_sparkle_bold.svg';
import {
  ErrorBanner,
  normalizePersonaTag,
  PersonaAvatar,
  PersonaTagChip,
  PersonaShell,
  PersonaState,
  TokenQuotaCtaHint,
} from '../components/PersonaShared';
import { useB2bTokenQuota } from '../hooks/useB2bTokenQuota';
import { isQuotaExceededError } from '../../../services/apiClient';
import { getQuotaExceededMessage } from '../../../utils/productAccess';
import {
  buildGeneratedPersonasSavePayload,
  buildGenerationPayload,
  buildSegmentSuggestionPayload,
  getGeneratedPersonaProfile,
  normalizeSegmentSuggestions,
  toPersonaLocale,
  validateGenerationInput,
  warnForGeneratedPersonaGaps,
} from '../services/personaGeneration';
import { buildPersonaDetail } from '../services/personaDetail';
import { personaApi, resolvePersonaApiErrorMessage } from '../services/personaService';
import { PersonaDetailReadOnlyView } from './PersonaDetailPage';
import { GenerationProgressView } from '../components/GenerationProgressView';
import { getPersonaDomainCopy } from '../constants/personaDomainCopy';
import '../styles/persona.scss';

const buildGenerationProgressStages = (domainCopy) => [
  {
    label: '입력 항목 분석',
    message: '입력하신 내용을 기반으로 가장 적합한 유저 모델을 분석합니다.',
  },
  {
    label: '성격 및 가치관 부여',
    message: domainCopy.generationProgressStageIdentity,
  },
  {
    label: '주요 행동 및 경험 작성',
    message: '퍼소나의 입체적인 배경 서사와 행동 양식을 구체화합니다.',
  },
  {
    label: '최종 퍼소나 생성',
    message: '구축된 서사를 바탕으로 최종 퍼소나를 생성 중입니다.',
  },
];
const MAX_CREATE_PERSONA_COUNT = 3;
const MAX_SEGMENT_COUNT = 3;

const clampPersonaCount = (value, maxValue = MAX_CREATE_PERSONA_COUNT) => {
  const numeric = Number(String(value ?? '').replace(/\D/g, ''));
  if (!Number.isFinite(numeric) || numeric < 1) return 1;
  return Math.min(Math.max(1, Number(maxValue) || 1), numeric);
};

const isCompleteSegment = (segment) => (
  String(segment.name || '').trim() && String(segment.definition || '').trim()
);

const emptyForm = {
  name: '',
  tag: '',
  title: '',
  gender: '',
  age: '',
  personality: '',
  language: 'ko',
  locale: 'ko-KR',
  folder_id: '',
  profile: {
    occupation: '',
    income: '',
    location: '',
    needs: '',
    pain_points: '',
  },
};

function toPayload(form) {
  return {
    ...form,
    tag: normalizePersonaTag(form.tag),
    age: form.age ? Number(form.age) : null,
    folder_id: form.folder_id ? Number(form.folder_id) : null,
    profile: Object.fromEntries(
      Object.entries(form.profile || {}).filter(([, value]) => String(value || '').trim() !== '')
    ),
    telecom_profile: Object.fromEntries(
      Object.entries(form.telecom_profile || {}).filter(([, value]) => String(value || '').trim() !== '')
    ),
  };
}

function asInputValue(value) {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  if (typeof value === 'object') return value.summary || value.description || JSON.stringify(value);
  return String(value);
}

function PersonaFormPage({ mode = 'create' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { personaId } = useParams();
  const { user } = useAppContext();
  const domainCopy = useMemo(() => getPersonaDomainCopy(user), [user]);
  const generationProgressStages = useMemo(
    () => buildGenerationProgressStages(domainCopy),
    [domainCopy],
  );
  const isEdit = mode === 'edit';
  const createFolderId = isEdit ? '' : String(location.state?.activeFolder || '');
  const [form, setForm] = useState(() => ({
    ...emptyForm,
    folder_id: createFolderId,
  }));
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState(0);
  const [error, setError] = useState('');
  const [ctaQuotaMessage, setCtaQuotaMessage] = useState('');
  const { remainingTokens, isQuotaExceeded, loading: quotaLoading } = useB2bTokenQuota();
  const quotaExceededMessage = useMemo(() => getQuotaExceededMessage(user), [user]);
  const [imageFile, setImageFile] = useState(null);
  const [generationForm, setGenerationForm] = useState({
    serviceDescription: '',
    targetAudience: '',
    segment: '',
    count: 1,
  });
  const [createTab, setCreateTab] = useState('service');
  const [segments, setSegments] = useState([
    { id: 1, name: '', definition: '', filters: '', count: 1 },
  ]);
  const [aiSegmentModalOpen, setAiSegmentModalOpen] = useState(false);
  const [aiSegmentPrompt, setAiSegmentPrompt] = useState('');
  const [generatingSegments, setGeneratingSegments] = useState(false);
  const [generationResult, setGenerationResult] = useState(null);
  const [selectedGeneratedPersonas, setSelectedGeneratedPersonas] = useState(() => new Set());
  const [generatedDetailIndex, setGeneratedDetailIndex] = useState(null);
  const [pendingGenerationLeave, setPendingGenerationLeave] = useState(null);
  const allowGenerationNavigationRef = useRef(false);

  const getPersonasListNavigationState = useCallback((extraState = {}) => {
    const folderId = form.folder_id ? String(form.folder_id) : '';
    return {
      ...extraState,
      ...(folderId ? { activeFolder: folderId } : {}),
    };
  }, [form.folder_id]);

  const navigateToPersonasList = useCallback((extraState = {}) => {
    navigate('/personas', { state: getPersonasListNavigationState(extraState) });
  }, [getPersonasListNavigationState, navigate]);

  const requestGenerationLeave = useCallback((action = { type: 'navigate', targetPath: '/personas' }) => {
    if (isEdit || !generating || allowGenerationNavigationRef.current) return true;
    setPendingGenerationLeave(action);
    return false;
  }, [generating, isEdit]);

  const confirmGenerationLeave = useCallback((targetPath = '/personas', proceed) => (
    requestGenerationLeave({ type: 'navigate', targetPath, proceed })
  ), [requestGenerationLeave]);

  const handleCancelPendingGenerationLeave = useCallback(() => {
    if (pendingGenerationLeave?.restoreHistoryOnCancel) {
      window.history.pushState({ personaGenerationGuard: true }, '', window.location.href);
    }
    setPendingGenerationLeave(null);
  }, [pendingGenerationLeave]);

  const handleConfirmPendingGenerationLeave = useCallback(() => {
    const action = pendingGenerationLeave;
    setPendingGenerationLeave(null);
    if (!action) return;
    allowGenerationNavigationRef.current = true;

    if (typeof action.proceed === 'function') {
      action.proceed();
      return;
    }

    if (action.type === 'history') {
      window.history.back();
      return;
    }

    navigate(action.targetPath || '/personas', {
      state: getPersonasListNavigationState(),
    });
  }, [getPersonasListNavigationState, navigate, pendingGenerationLeave]);

  const loadData = useCallback(async () => {
    setError('');
    try {
      if (isEdit && personaId) {
        const persona = await personaApi.getPersona(personaId);
        setForm({
          ...emptyForm,
          ...persona,
          age: persona.age || '',
          folder_id: persona.folder_id || '',
          profile: {
            ...emptyForm.profile,
            ...(persona.profile || {}),
          },
        });
      }
    } catch (err) {
      setError(err.message || '화면을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [isEdit, personaId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!generating) {
      setGenerationStep(0);
      return undefined;
    }

    const schedule = [
      { step: 1, delay: 9000 },
      { step: 2, delay: 28000 },
      { step: 3, delay: 65000 },
    ];
    const timers = schedule.map(({ step, delay }) =>
      window.setTimeout(() => {
        setGenerationStep((current) => Math.max(current, step));
      }, delay)
    );
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [generating]);

  useEffect(() => {
    if (isEdit || !generating) return undefined;
    window.history.pushState({ personaGenerationGuard: true }, '', window.location.href);
    const handlePopState = () => {
      if (allowGenerationNavigationRef.current) return;
      setPendingGenerationLeave({
        type: 'history',
        restoreHistoryOnCancel: true,
      });
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [generating, isEdit]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const setProfileField = (field, value) => {
    setForm((prev) => ({ ...prev, profile: { ...prev.profile, [field]: value } }));
  };

  const setTelecomProfileField = (field, value) => {
    setForm((prev) => ({ ...prev, telecom_profile: { ...(prev.telecom_profile || {}), [field]: value } }));
  };

  const attachImageIfNeeded = async (savedPersonaId) => {
    if (!imageFile) return;
    const asset = await personaApi.uploadAsset(imageFile, 'persona_image');
    await personaApi.attachPersonaImage(savedPersonaId, { asset_id: asset.id });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setError('이름을 입력해 주세요.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = toPayload(form);
      const saved = isEdit
        ? await personaApi.updatePersona(personaId, payload)
        : await personaApi.createPersona(payload);
      await attachImageIfNeeded(saved.id || personaId);
      navigate(`/personas/${saved.id || personaId}`);
    } catch (err) {
      setError(err.message || 'Persona 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const ctaQuotaHintMessage = ctaQuotaMessage || (isQuotaExceeded ? quotaExceededMessage : '');
  const isCtaQuotaBlocked = Boolean(ctaQuotaMessage || isQuotaExceeded);

  const handleGenerate = async (event) => {
    event.preventDefault();
    if (isCtaQuotaBlocked) {
      setCtaQuotaMessage(quotaExceededMessage);
      setError('');
      return;
    }
    const validationErrors = validateGenerationInput({ createTab, generationForm, segments });
    if (validationErrors.length > 0) {
      setError(validationErrors.join('\n'));
      return;
    }

    setGenerating(true);
    setGenerationStep(0);
    setError('');
    setCtaQuotaMessage('');
    try {
      const payload = buildGenerationPayload({
        createTab,
        generationForm,
        segments,
        locale: toPersonaLocale(form.locale),
      });
      const generated = await personaApi.generatePersonas(payload);
      const personas = Array.isArray(generated?.personas) ? generated.personas : [];
      if (personas.length === 0) {
        setGenerationResult(null);
        setSelectedGeneratedPersonas(new Set());
        setGeneratedDetailIndex(null);
        setError('생성된 persona가 없습니다. 입력값을 확인한 뒤 다시 시도해 주세요.');
        return;
      }
      warnForGeneratedPersonaGaps(personas);
      setGenerationResult({ ...generated, requestPayload: payload });
      setSelectedGeneratedPersonas(new Set(personas.map((_, index) => String(index))));
      setGeneratedDetailIndex(null);
    } catch (err) {
      if (isQuotaExceededError(err)) {
        setCtaQuotaMessage(quotaExceededMessage);
        setError('');
      } else {
        setError(resolvePersonaApiErrorMessage(err, 'AI persona 생성에 실패했습니다.'));
      }
    } finally {
      setGenerating(false);
    }
  };

  const toggleGeneratedPersona = (index) => {
    setSelectedGeneratedPersonas((current) => {
      const next = new Set(current);
      const key = String(index);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSaveGenerated = async () => {
    const personas = Array.isArray(generationResult?.personas) ? generationResult.personas : [];
    const selectedPersonas = personas.filter((_, index) => selectedGeneratedPersonas.has(String(index)));
    if (selectedPersonas.length === 0) {
      setError('저장할 persona를 선택해 주세요.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const saved = await personaApi.saveGeneratedPersonas(buildGeneratedPersonasSavePayload({
        selectedPersonas,
        generationResult,
        folderId: form.folder_id,
        fallbackLocale: form.locale,
      }));
      navigateToPersonasList({
        toast: { message: `${saved.length}명 퍼소나를 저장했습니다.` },
      });
    } catch (err) {
      setError(err.message || '선택한 persona 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const setCreateCount = (value) => {
    const next = clampPersonaCount(value);
    setGenerationForm((prev) => ({ ...prev, count: next }));
  };

  const updateSegment = (segmentId, field, value) => {
    setSegments((current) => {
      const otherPersonaCount = current.reduce((sum, segment) => (
        segment.id === segmentId ? sum : sum + (Number(segment.count) || 0)
      ), 0);
      const maxSegmentPersonaCount = Math.max(0, MAX_CREATE_PERSONA_COUNT - otherPersonaCount);

      return current.map((segment) => (
        segment.id === segmentId
          ? {
            ...segment,
            [field]: field === 'count'
              ? clampPersonaCount(value, maxSegmentPersonaCount || 1)
              : value,
          }
          : segment
      ));
    });
  };

  const addSegment = () => {
    setSegments((current) => {
      const currentPersonaCount = current.reduce((sum, segment) => sum + (Number(segment.count) || 0), 0);
      if (current.length >= MAX_SEGMENT_COUNT || currentPersonaCount >= MAX_CREATE_PERSONA_COUNT) return current;
      return [
        ...current,
        { id: Date.now(), name: '', definition: '', filters: '', count: 1 },
      ];
    });
  };

  const removeSegment = (segmentId) => {
    setSegments((current) => current.length <= 1 ? current : current.filter((segment) => segment.id !== segmentId));
  };

  const openAiSegmentModal = () => {
    setAiSegmentPrompt('');
    setAiSegmentModalOpen(true);
  };

  const handleAiSegmentSubmit = async (event) => {
    event.preventDefault();
    if (isCtaQuotaBlocked) {
      setCtaQuotaMessage(quotaExceededMessage);
      setError('');
      return;
    }
    const prompt = aiSegmentPrompt.trim();
    if (prompt.length < 10) {
      setError('세그먼트 초안 생성을 위해 최소 10자 이상의 컨텍스트를 입력해 주세요.');
      return;
    }

    setGeneratingSegments(true);
    setAiSegmentModalOpen(false);
    setError('');
    setCtaQuotaMessage('');
    try {
      const response = await personaApi.generateSegments(buildSegmentSuggestionPayload({
        context: prompt,
        locale: toPersonaLocale(form.locale),
      }));
      const suggestedSegments = normalizeSegmentSuggestions(response.segments, MAX_CREATE_PERSONA_COUNT);
      if (suggestedSegments.length === 0) {
        setError('생성된 세그먼트가 없습니다. 입력값을 확인한 뒤 다시 시도해 주세요.');
        return;
      }
      setSegments(suggestedSegments);
      setCreateTab('segment');
      setAiSegmentPrompt('');
    } catch (err) {
      setAiSegmentModalOpen(true);
      if (isQuotaExceededError(err)) {
        setCtaQuotaMessage(quotaExceededMessage);
        setError('');
      } else {
        setError(resolvePersonaApiErrorMessage(err, '세그먼트 추천에 실패했습니다.'));
      }
    } finally {
      setGeneratingSegments(false);
    }
  };

  const addAiSegment = () => {
    openAiSegmentModal();
  };

  if (loading) {
    return (
      <PersonaShell title="Persona 편집" eyebrow="Persona">
        <PersonaState type="loading" title="불러오는 중" message="Persona 정보를 가져오고 있습니다." />
      </PersonaShell>
    );
  }

  if (!isEdit) {
    const completeSegments = segments.filter(isCompleteSegment);
    const segmentPersonaCount = segments.reduce((sum, segment) => sum + (Number(segment.count) || 0), 0);
    const createCount = createTab === 'segment'
      ? completeSegments.reduce((sum, segment) => sum + (Number(segment.count) || 0), 0)
      : Number(generationForm.count) || 0;
    const generationValidationErrors = validateGenerationInput({ createTab, generationForm, segments });
    const canGenerate = generationValidationErrors.length === 0;
    const generatedPersonas = Array.isArray(generationResult?.personas) ? generationResult.personas : [];
    const selectedGeneratedCount = generatedPersonas.filter((_, index) => selectedGeneratedPersonas.has(String(index))).length;
    const serviceDescriptionError = createTab === 'service' && generationForm.serviceDescription.trim().length > 0 && generationForm.serviceDescription.trim().length < 10
      ? '서비스 정의 및 주요 기능을 10자 이상 입력해 주세요.'
      : '';

    if (generating) {
      return (
        <B2BLayout
          hideCollapseButton={false}
          defaultCollapsed={true}
          hidePageHeader={true}
          navigationGuard={confirmGenerationLeave}
        >
          <main className="persona-generation-progress">
            <GenerationProgressView
              stages={generationProgressStages}
              currentStep={generationStep}
            />
          </main>
          <PageLeaveConfirmModal
            isOpen={Boolean(pendingGenerationLeave)}
            message="퍼소나 생성이 진행 중입니다."
            subMessage="나가기를 선택하면 생성 중인 결과는 저장되지 않습니다."
            onCancel={handleCancelPendingGenerationLeave}
            onConfirm={handleConfirmPendingGenerationLeave}
          />
        </B2BLayout>
      );
    }

    if (generatedPersonas.length > 0) {
      const generatedDetailPersona = generatedDetailIndex !== null ? generatedPersonas[generatedDetailIndex] : null;
      return (
        <B2BLayout hideCollapseButton={false} defaultCollapsed={true} hidePageHeader={true}>
          <main className="persona-create-screen">
            <header className="persona-create-title">
              <button type="button" aria-label="목록으로" onClick={() => navigateToPersonasList()}>
                <ChevronLeft size={24} strokeWidth={1.8} />
              </button>
              <div>
                <h1>새 퍼소나 만들기</h1>
                <p>AI를 활용해 서비스에 맞는 퍼소나를 자동으로 생성합니다</p>
              </div>
            </header>

            {generatedDetailPersona ? (
              <GeneratedPersonaDetail
                persona={generatedDetailPersona}
                onBack={() => setGeneratedDetailIndex(null)}
              />
            ) : (
              <section className="persona-generated-results">
                <div className="persona-generated-results__content">
                  <ErrorBanner message={error} />
                  <p className="persona-generated-results__intro">
                    AI가 분석한 퍼소나들을 확인해 보세요. 사용할 퍼소나를 선택하면 퍼소나 목록에 저장됩니다.
                  </p>
                  <GeneratedPersonaPreview
                    personas={generatedPersonas}
                    selected={selectedGeneratedPersonas}
                    onToggle={toggleGeneratedPersona}
                    onOpenDetail={setGeneratedDetailIndex}
                  />
                </div>
              </section>
            )}

            {generatedDetailPersona ? null : (
              <footer className="persona-create-cta persona-create-cta--results">
                <div>
                  <button
                    type="button"
                    className="persona-create-cta__secondary"
                    onClick={() => {
                      setGenerationResult(null);
                      setSelectedGeneratedPersonas(new Set());
                      setGeneratedDetailIndex(null);
                    }}
                  >
                    이전
                  </button>
                  <button
                    type="button"
                    disabled={selectedGeneratedCount === 0 || saving}
                    onClick={handleSaveGenerated}
                  >
                    {saving ? '저장 중...' : `${selectedGeneratedCount || 'N'}명 퍼소나 저장`}
                  </button>
                </div>
              </footer>
            )}
            {saving ? (
              <div className="persona-segment-loading">
                <div className="persona-spinner" />
                <p>퍼소나를 저장 중입니다.</p>
              </div>
            ) : null}
          </main>
        </B2BLayout>
      );
    }

    return (
      <B2BLayout hideCollapseButton={false} defaultCollapsed={true} hidePageHeader={true}>
        <main className="persona-create-screen">
          <header className="persona-create-title">
            <button type="button" aria-label="목록으로" onClick={() => navigateToPersonasList()}>
              <ChevronLeft size={24} strokeWidth={1.8} />
            </button>
            <div>
              <h1>새 퍼소나 만들기</h1>
              <p>AI를 활용해 서비스에 맞는 퍼소나를 자동으로 생성합니다</p>
            </div>
          </header>

          <form className="persona-create-body" onSubmit={handleGenerate}>
            <div className="persona-create-content">
              <ErrorBanner message={error} />

              <div className="persona-create-tabs">
                <button
                  type="button"
                  className={createTab === 'service' ? 'active' : ''}
                  onClick={() => setCreateTab('service')}
                >
                  서비스/제품 기반
                </button>
                <button
                  type="button"
                  className={createTab === 'segment' ? 'active' : ''}
                  onClick={() => setCreateTab('segment')}
                >
                  타겟 세그먼트 지정
                </button>
              </div>

              {createTab === 'service' ? (
                <ServiceCreateFields
                  form={generationForm}
                  serviceDescriptionError={serviceDescriptionError}
                  onChange={setGenerationForm}
                  onCountChange={setCreateCount}
                  copy={domainCopy}
                />
              ) : (
                <SegmentCreateFields
                  segments={segments}
                  maxSegments={MAX_SEGMENT_COUNT}
                  maxPersonaCount={MAX_CREATE_PERSONA_COUNT}
                  totalPersonaCount={segmentPersonaCount}
                  onChange={updateSegment}
                  onAdd={addSegment}
                  onAiAdd={addAiSegment}
                  onRemove={removeSegment}
                  copy={domainCopy}
                />
              )}

            </div>

            <footer className="persona-create-cta">
              <div>
                {generatedPersonas.length > 0 ? (
                  <p>선택한 퍼소나: {selectedGeneratedCount}/{generatedPersonas.length}명</p>
                ) : null}
                {!generatedPersonas.length ? (
                  <TokenQuotaCtaHint
                    remainingTokens={remainingTokens}
                    loading={quotaLoading}
                    warningMessage={isCtaQuotaBlocked ? ctaQuotaHintMessage : ''}
                  />
                ) : null}
                {generatedPersonas.length > 0 ? (
                  <button type="button" disabled={selectedGeneratedCount === 0 || saving} onClick={handleSaveGenerated}>
                    {saving ? '저장 중...' : `선택한 ${selectedGeneratedCount || 0}명 저장`}
                  </button>
                ) : (
                  <button type="submit" disabled={!canGenerate || isCtaQuotaBlocked}>
                    퍼소나 {createCount || 'N'}명 생성
                  </button>
                )}
              </div>
            </footer>
          </form>

          {aiSegmentModalOpen ? (
            <AiSegmentModal
              value={aiSegmentPrompt}
              onChange={setAiSegmentPrompt}
              copy={domainCopy}
              onCancel={() => {
                setAiSegmentPrompt('');
                setAiSegmentModalOpen(false);
              }}
              onSubmit={handleAiSegmentSubmit}
            />
          ) : null}

          {generatingSegments ? (
            <div className="persona-segment-loading">
              <div className="persona-spinner" />
              <p>세그먼트를 생성 중입니다.</p>
            </div>
          ) : null}
        </main>
      </B2BLayout>
    );
  }

  const profile = form.profile || {};
  const telecom = form.telecom_profile || {};

  return (
    <B2BLayout hideCollapseButton={false} defaultCollapsed={true} hidePageHeader={true}>
      <main className="persona-edit-screen">
        <header className="persona-create-title persona-edit-title">
          <button type="button" aria-label="상세로" onClick={() => navigate(`/personas/${personaId}`)}>
            <ChevronLeft size={24} strokeWidth={1.8} />
          </button>
          <div>
            <h1>퍼소나 수정</h1>
          </div>
        </header>

        <section className="persona-edit-body">
          <form className="persona-edit-content" onSubmit={handleSubmit}>
            <ErrorBanner message={error} />

            <div className="persona-edit-user">
              <div className="persona-edit-user__profile">
                <label className="persona-edit-avatar" title="이미지 변경">
                  <PersonaAvatar persona={form} size="sm" />
                  <input type="file" accept="image/*" onChange={(event) => setImageFile(event.target.files?.[0] || null)} />
                </label>
                <EditTextInput value={form.name} onChange={(value) => setField('name', value)} />
              </div>
              <div className="persona-edit-actions">
                <button type="button" onClick={() => navigate(`/personas/${personaId}`)}>수정 취소</button>
                <button type="submit" disabled={saving}>{saving ? '저장 중' : '수정 완료'}</button>
              </div>
            </div>

            <div className="persona-edit-two">
              <EditInfoCard
                title="기본 정보"
                rows={[
                  ['나이', form.age || '', (value) => setField('age', value)],
                  ['태그', form.tag || '', (value) => setField('tag', value)],
                  ['세대', profile.generation || '', (value) => setProfileField('generation', value)],
                  ['성별', form.gender || '', (value) => setField('gender', value)],
                  ['거주', profile.location || profile.currentCity || '', (value) => setProfileField('location', value)],
                  ['연소득', profile.income || '', (value) => setProfileField('income', value)],
                ]}
              />
              <EditInfoCard
                title="직업 정보"
                rows={[
                  ['직함', form.title || '', (value) => setField('title', value)],
                  ['산업', profile.industry || profile.sector || '', (value) => setProfileField('industry', value)],
                  ['소속', profile.company || profile.organisation || '', (value) => setProfileField('company', value)],
                  ['직급', profile.position || profile.roleLevel || '', (value) => setProfileField('position', value)],
                  ['직무 영역', profile.role || profile.roleArea || '', (value) => setProfileField('role', value)],
                ]}
              />
            </div>

            <section className="persona-edit-card persona-edit-dna">
              <h2>통신서비스 DNA</h2>
              <div className="persona-edit-dna__body">
                <div className="persona-edit-dna__chart" aria-hidden="true" />
                <div className="persona-edit-dna__list">
                  {[
                    ['핵심요약', telecom.telecomUsage || '', (value) => setTelecomProfileField('telecomUsage', value)],
                    ['멤버십 혜택 민감도', telecom.membershipSensitivity || '', (value) => setTelecomProfileField('membershipSensitivity', value)],
                    ['통신서비스 비용 민감도', telecom.costSensitivity || '', (value) => setTelecomProfileField('costSensitivity', value)],
                    ['디지털 신뢰도', telecom.digitalTrust || '', (value) => setTelecomProfileField('digitalTrust', value)],
                    ['신규서비스 수용도', telecom.serviceAcceptance || '', (value) => setTelecomProfileField('serviceAcceptance', value)],
                  ].map(([label, value, onChange]) => (
                    <EditInlineRow key={label} label={label} value={asInputValue(value)} onChange={onChange} />
                  ))}
                </div>
              </div>
            </section>

            <section className="persona-edit-card persona-edit-ux">
              <h2>UX 태도</h2>
              <div className="persona-edit-chip-row">
                {['서비스 신청', '서비스 이용 시작', '탐색 및 검색', '선택 및 의사결정', '서비스 화면에서의 반응', '요금제/분석 확인'].map((chip, index) => (
                  <span key={chip} className={index === 0 ? 'active' : ''}>{chip}</span>
                ))}
              </div>
              <EditTextArea
                value={asInputValue(telecom.uxInteraction || profile.attitudes)}
                onChange={(value) => setTelecomProfileField('uxInteraction', value)}
                maxLength={150}
              />
              <label className="persona-edit-select">
                <span>리서치/UX 태도 선택</span>
                <select
                  value={asInputValue(profile.uxPriority)}
                  onChange={(event) => setProfileField('uxPriority', event.target.value)}
                >
                  <option value="">선택해 주세요.</option>
                  <option value="혜택 민감형">혜택 민감형</option>
                  <option value="비용 절감형">비용 절감형</option>
                  <option value="디지털 신뢰형">디지털 신뢰형</option>
                </select>
              </label>
            </section>

            <EditTextAreaCard
              title="성격 및 행동"
              fields={[
                ['성격', form.personality || '', (value) => setField('personality', value)],
                ['행동 패턴', profile.behavior_pattern || profile.behaviours || '', (value) => setProfileField('behavior_pattern', value)],
                ['동기부여', profile.motivation || '', (value) => setProfileField('motivation', value)],
                ['가치관/태도', profile.values || profile.attitudes || '', (value) => setProfileField('values', value)],
              ]}
            />

            <EditTextAreaCard
              title="상세 프로필"
              fields={[
                ['문화적 배경', profile.cultural_background || profile.culturalBackground || '', (value) => setProfileField('cultural_background', value)],
                ['성장 배경', profile.growth_background || profile.upbringing || '', (value) => setProfileField('growth_background', value)],
                ['이력', profile.history || profile.biography || '', (value) => setProfileField('history', value)],
                ['거주이력', profile.residence_history || '', (value) => setProfileField('residence_history', value)],
                ['관심사/취미', profile.interests || '', (value) => setProfileField('interests', value)],
                ['인맥/관계', profile.relationships || profile.socialContext || '', (value) => setProfileField('relationships', value)],
                ['선호도', profile.preferences || '', (value) => setProfileField('preferences', value)],
                ['말투/스타일', profile.speech_style || profile.demeanour || '', (value) => setProfileField('speech_style', value)],
              ]}
            />
          </form>
        </section>
      </main>
    </B2BLayout>
  );
}

function GeneratedPersonaPreview({ personas, selected, onToggle, onOpenDetail }) {
  return (
    <section className="persona-generated-preview">
      <div className="persona-generated-preview__grid">
        {personas.map((persona, index) => {
          const selectedKey = String(index);
          const isSelected = selected.has(selectedKey);
          const profile = getGeneratedPersonaProfile(persona);
          const meta = [
            profile.gender,
            profile.age ? `${profile.age}세` : '',
            profile.job,
          ].filter(Boolean).join(' · ');
          const normalizedPersona = {
            ...persona,
            image_url: persona.image_url || persona.imageUrl,
          };
          return (
            <article
              key={`${persona.name || 'persona'}-${index}`}
              className={`persona-generated-card ${isSelected ? 'selected' : ''}`}
            >
              <label className="persona-generated-card__select" aria-label={`${persona.name || `퍼소나 ${index + 1}`} 선택`}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => onToggle(index)}
                />
                <span className="persona-generated-card__check" />
              </label>
              <button
                type="button"
                className="persona-generated-card__body"
                onClick={() => onOpenDetail(index)}
              >
                <div className="persona-generated-card__headline">
                  <PersonaAvatar persona={normalizedPersona} size="sm" />
                  <div className="persona-generated-card__identity">
                    <div className="persona-generated-card__identity-main">
                      <strong>{persona.name || `퍼소나 ${index + 1}`}</strong>
                      <PersonaTagChip tag={persona.tag} />
                    </div>
                    {meta ? <p className="persona-generated-card__meta">{meta}</p> : null}
                  </div>
                  <span className="persona-generated-card__arrow" aria-hidden="true">
                    <ChevronRight size={18} strokeWidth={1.8} />
                  </span>
                </div>
                <div className="persona-generated-card__divider" />
                <PersonaGeneratedSection title="성격" body={profile.personality} />
                <PersonaGeneratedSection title="행동 패턴" body={profile.behavior} />
              </button>
            </article>
          );
        })}
      </div>
    </section>
  );
}

const normalizeGeneratedPersonaForDetail = (persona = {}) => {
  const profile = persona.profile || {};
  const telecomProfile = persona.telecom_profile || {};
  const telecomBehaviorDimensions = (
    persona.telecomBehaviorDimensions ||
    persona.telecom_behavior_dimensions ||
    telecomProfile.telecomBehaviorDimensions ||
    telecomProfile.telecom_behavior_dimensions ||
    profile.telecomBehaviorDimensions ||
    profile.telecom_behavior_dimensions
  );
  const telecomBehaviorScores = (
    persona.telecomBehaviorScores ||
    persona.telecom_behavior_scores ||
    telecomProfile.telecomBehaviorScores ||
    telecomProfile.telecom_behavior_scores
  );

  return {
    ...persona,
    image_url: persona.image_url || persona.imageUrl,
    currentCity: persona.currentCity ?? persona.current_city ?? profile.currentCity ?? profile.current_city,
    currentCountry: persona.currentCountry ?? persona.current_country ?? profile.currentCountry ?? profile.current_country,
    roleArea: persona.roleArea ?? persona.role_area ?? profile.roleArea ?? profile.role_area,
    roleLevel: persona.roleLevel ?? persona.role_level ?? profile.roleLevel ?? profile.role_level,
    socialContext: persona.socialContext ?? persona.social_context ?? profile.socialContext ?? profile.social_context,
    culturalBackground: persona.culturalBackground ?? persona.cultural_background ?? profile.culturalBackground ?? profile.cultural_background,
    telecomBehaviorDimensions,
    telecomBehaviorScores,
    profile: {
      ...profile,
      currentCity: profile.currentCity ?? profile.current_city ?? persona.currentCity ?? persona.current_city,
      currentCountry: profile.currentCountry ?? profile.current_country ?? persona.currentCountry ?? persona.current_country,
      roleArea: profile.roleArea ?? profile.role_area ?? persona.roleArea ?? persona.role_area,
      roleLevel: profile.roleLevel ?? profile.role_level ?? persona.roleLevel ?? persona.role_level,
      socialContext: profile.socialContext ?? profile.social_context ?? persona.socialContext ?? persona.social_context,
      culturalBackground: profile.culturalBackground ?? profile.cultural_background ?? persona.culturalBackground ?? persona.cultural_background,
      telecomBehaviorDimensions,
    },
    telecom_profile: {
      ...telecomProfile,
      telecomBehaviorDimensions,
      telecomBehaviorScores,
    },
  };
};

function GeneratedPersonaDetail({ persona, onBack }) {
  const [activeTelecomScoreKey, setActiveTelecomScoreKey] = useState('brandRetention');
  const normalizedPersona = normalizeGeneratedPersonaForDetail(persona);
  const detail = buildPersonaDetail(normalizedPersona);

  return (
    <section className="persona-generated-detail">
      <div className="persona-generated-detail__content persona-detail-content">
        <button type="button" className="persona-generated-detail__back" onClick={onBack}>
          <ChevronLeft size={18} strokeWidth={1.8} />
          생성 결과 목록
        </button>
        <PersonaDetailReadOnlyView
          detailSource={normalizedPersona}
          detail={detail}
          activeTelecomScoreKey={activeTelecomScoreKey}
          onActiveTelecomScoreKeyChange={setActiveTelecomScoreKey}
        />
      </div>
    </section>
  );
}

function PersonaGeneratedSection({ title, body }) {
  return (
    <div className="persona-generated-card__section">
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  );
}

function EditTextInput({ value, onChange, type = 'text' }) {
  return (
    <input
      className="persona-edit-input"
      type={type}
      value={asInputValue(value)}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function EditInlineRow({ label, value, onChange }) {
  return (
    <div className="persona-edit-inline-row">
      <span>{label}</span>
      <EditTextInput value={value} onChange={onChange} />
    </div>
  );
}

function EditInfoCard({ title, rows }) {
  return (
    <section className="persona-edit-card persona-edit-info-card">
      <h2>{title}</h2>
      <div className="persona-edit-info-card__rows">
        {rows.map(([label, value, onChange]) => (
          <EditInlineRow key={label} label={label} value={value} onChange={onChange} />
        ))}
      </div>
    </section>
  );
}

function EditTextArea({ value, onChange, maxLength = 150 }) {
  const textValue = asInputValue(value);
  return (
    <label className="persona-edit-textarea">
      <textarea
        value={textValue}
        maxLength={maxLength}
        onChange={(event) => onChange(event.target.value)}
      />
      <span>{textValue.length}/{maxLength}</span>
    </label>
  );
}

function EditTextAreaCard({ title, fields }) {
  return (
    <section className="persona-edit-card persona-edit-textarea-card">
      <h2>{title}</h2>
      <div className="persona-edit-textarea-grid">
        {fields.map(([label, value, onChange]) => (
          <div key={label} className="persona-edit-textarea-field">
            <span>{label}</span>
            <EditTextArea value={value} onChange={onChange} />
          </div>
        ))}
      </div>
    </section>
  );
}


function ServiceCreateFields({ form, serviceDescriptionError, onChange, onCountChange, copy }) {
  return (
    <div className="persona-create-fields">
      <CreateTextarea
        required
        label="서비스 정의 및 주요 기능"
        value={form.serviceDescription}
        onChange={(value) => onChange((prev) => ({ ...prev, serviceDescription: value }))}
        placeholder={copy.serviceDescriptionPlaceholder}
        error={serviceDescriptionError}
      />
      <CreateTextarea
        label="가설 고객층 (선택)"
        value={form.targetAudience}
        onChange={(value) => onChange((prev) => ({ ...prev, targetAudience: value }))}
        placeholder={copy.targetAudiencePlaceholder}
      />
      <CreateCountControl
        label="생성할 퍼소나 수"
        value={form.count}
        onChange={onCountChange}
      />
    </div>
  );
}

function SegmentCreateFields({
  segments,
  maxSegments,
  maxPersonaCount,
  totalPersonaCount,
  onChange,
  onAdd,
  onAiAdd,
  onRemove,
  copy,
}) {
  const addDisabled = segments.length >= maxSegments || totalPersonaCount >= maxPersonaCount;

  return (
    <div className="persona-create-segments">
      {segments.map((segment, index) => (
        <section key={segment.id} className="persona-create-segment-card">
          <div className="persona-create-segment-card__header">
            <h2>세그먼트 {index + 1}</h2>
            {index > 0 ? (
              <button type="button" aria-label="세그먼트 삭제" onClick={() => onRemove(segment.id)}>
                <X size={18} />
              </button>
            ) : null}
          </div>
          <CreateInput
            required
            label="세그먼트 명칭"
            value={segment.name}
            onChange={(value) => onChange(segment.id, 'name', value)}
            placeholder={copy.segmentNamePlaceholder}
            maxLength={20}
          />
          <CreateTextarea
            required
            label="세그먼트 정의 및 퍼소나 특성"
            value={segment.definition}
            onChange={(value) => onChange(segment.id, 'definition', value)}
            placeholder={copy.segmentDefinitionPlaceholder}
            error={(() => {
              const trimmed = String(segment.definition || '').trim();
              return trimmed.length > 0 && trimmed.length < 10
                ? '세그먼트 정의 및 퍼소나 특성을 10자 이상 입력해 주세요.'
                : '';
            })()}
          />
          <CreateTextarea
            label="필터링 조건 (선택)"
            value={segment.filters}
            onChange={(value) => onChange(segment.id, 'filters', value)}
            placeholder="AI가 반영해야 할 추가 조건을 입력해 주세요. (예: SKT 10년 이상 사용, 가족 결합 3인 이상, 한 달 평균 데이터 소모량 100GB 이상)"
          />
          <CreateCountControl
            label="세그먼트 퍼소나 수"
            value={segment.count}
            maxValue={Math.max(1, maxPersonaCount - (totalPersonaCount - (Number(segment.count) || 0)))}
            onChange={(value) => onChange(segment.id, 'count', value)}
          />
        </section>
      ))}
      <div className="persona-create-segment-actions">
        <button type="button" disabled={addDisabled} onClick={onAdd}>
          <Plus size={24} />
          세그먼트 직접 추가
        </button>
        <button type="button" onClick={onAiAdd}>
          <img className="persona-sparkle-icon" src={sparkleBoldIcon} alt="" aria-hidden="true" />
          AI로 세그먼트 자동 생성
        </button>
      </div>
    </div>
  );
}

function AiSegmentModal({ value, onChange, onCancel, onSubmit, copy }) {
  const [touched, setTouched] = useState(false);
  const trimmedLength = String(value || '').trim().length;
  const showLengthError = touched && trimmedLength > 0 && trimmedLength < 10;
  const lengthErrorMessage = '리서치 목적을 10자 이상 입력해 주세요.';

  return (
    <div className="persona-modal-backdrop" role="presentation">
      <form className="persona-ai-segment-modal" onSubmit={onSubmit} onClick={(event) => event.stopPropagation()}>
        <header>
          <img className="persona-sparkle-icon" src={sparkleBoldIcon} alt="" aria-hidden="true" />
          <h2>AI로 세그먼트 자동 생성</h2>
        </header>
        <p>
          서비스의 특징이나 리서치 목적을 간단히 알려주세요.
          <br />
          AI가 리서치에 적합한 세그먼트 구성을 자동으로 생성해 드립니다.
        </p>
        <Textarea
          className="persona-ai-segment-modal__textarea"
          textareaClassName="persona-ai-segment-modal__textarea-control"
          value={value}
          maxLength={150}
          showCount
          invalid={showLengthError}
          error={showLengthError ? lengthErrorMessage : ''}
          onChange={(event) => onChange(event.target.value.slice(0, 150))}
          onBlur={() => setTouched(true)}
          aria-label="AI 세그먼트 자동 생성 설명"
          placeholder={copy.researchGoalPlaceholder}
          autoFocus
        />
        <footer>
          <button type="button" onClick={onCancel}>취소하기</button>
          <button type="submit" disabled={trimmedLength < 10}>생성하기</button>
        </footer>
      </form>
    </div>
  );
}

function CreateInput({ label, value, onChange, placeholder, required = false, maxLength }) {
  return (
    <label className="persona-create-field">
      <span>{label}{required ? <em /> : null}</span>
      <input
        value={value}
        maxLength={maxLength}
        onChange={(event) => onChange(maxLength ? event.target.value.slice(0, maxLength) : event.target.value)}
        placeholder={placeholder}
      />
      {maxLength ? <small>{String(value || '').length}/{maxLength}</small> : null}
    </label>
  );
}

function CreateTextarea({ label, value, onChange, placeholder, required = false, error = '' }) {
  return (
    <label className="persona-create-field">
      <span>{label}{required ? <em /> : null}</span>
      <textarea value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />
      {error ? <small className="persona-create-field__error">{error}</small> : null}
    </label>
  );
}

function CreateCountControl({ label, value, onChange, disabled = false, maxValue = MAX_CREATE_PERSONA_COUNT }) {
  const normalizedMaxValue = Math.max(1, Number(maxValue) || 1);
  const numericValue = clampPersonaCount(value, normalizedMaxValue);

  return (
    <div className="persona-create-count">
      <label>{label}<em /></label>
      <p>한번에 최대 3명까지 생성가능합니다.</p>
      <div>
        <button type="button" disabled={disabled || numericValue <= 1} onClick={() => onChange(numericValue - 1)}>
          <Minus size={24} />
        </button>
        <div>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={numericValue}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value.replace(/\D/g, ''))}
            onBlur={(event) => onChange(clampPersonaCount(event.target.value, normalizedMaxValue))}
          />
          <span>명</span>
        </div>
        <button type="button" disabled={disabled || numericValue >= normalizedMaxValue} onClick={() => onChange(numericValue + 1)}>
          <Plus size={24} />
        </button>
      </div>
    </div>
  );
}

export default PersonaFormPage;
